import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronLeftIcon,
  PencilSquareIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  MinusCircleIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/Button";
import { Select } from "../components/ui/Select";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import { activitiesApi } from "../api/activities";
import { completionsApi } from "../api/completions";
import { formatCabinNumber, normalizeDivisionName } from "../utils/terminology";
import { isCamperCabin } from "../utils/cabinFilter";
import { sortAcs } from "../utils/acSort";
import toast from "react-hot-toast";
import type { ActivityRoster, RosterCamper, AchievementCredit, CompletionStatus, RosterCompletion } from "../types";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => ({
  value: currentYear - i,
  label: String(currentYear - i),
}));

// ─── AC status badge ─────────────────────────────────────────────────────────

function AcBadge({
  ac,
  completion,
  editMode,
  onClick,
}: {
  ac: AchievementCredit;
  completion: RosterCompletion | null;
  editMode: boolean;
  onClick?: () => void;
}) {
  const base = "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium select-none transition-colors";
  const interactable = editMode ? "cursor-pointer hover:opacity-80 active:scale-95" : "";

  if (!completion) {
    return (
      <button
        type="button"
        disabled={!editMode}
        onClick={onClick}
        className={`${base} bg-stone-100 text-stone-500 border border-stone-200 ${interactable}`}
        title={ac.name}
      >
        <MinusCircleIcon className="w-3.5 h-3.5 shrink-0" />
        {ac.displayNumber}
      </button>
    );
  }

  if (completion.status === "completed") {
    return (
      <button
        type="button"
        disabled={!editMode}
        onClick={onClick}
        className={`${base} bg-forest-100 text-forest-700 border border-forest-200 ${interactable}`}
        title={ac.name}
      >
        <CheckCircleSolid className="w-3.5 h-3.5 shrink-0" />
        {ac.displayNumber}
      </button>
    );
  }

  if (completion.status === "pending") {
    return (
      <button
        type="button"
        disabled={!editMode}
        onClick={onClick}
        className={`${base} bg-amber-100 text-amber-700 border border-amber-200 ${interactable}`}
        title={ac.name}
      >
        <ClockIcon className="w-3.5 h-3.5 shrink-0" />
        {ac.displayNumber}
      </button>
    );
  }

  // excused
  return (
    <button
      type="button"
      disabled={!editMode}
      onClick={onClick}
      className={`${base} bg-stone-100 text-stone-400 border border-stone-200 line-through ${interactable}`}
      title={ac.name}
    >
      <XCircleIcon className="w-3.5 h-3.5 shrink-0" />
      {ac.displayNumber}
    </button>
  );
}

// ─── Graduation badge ─────────────────────────────────────────────────────────

function GraduationBadge({
  graduated,
  editMode,
  onClick,
}: {
  graduated: boolean;
  editMode: boolean;
  onClick?: () => void;
}) {
  const base = "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium select-none transition-colors";
  const interactable = editMode ? "cursor-pointer hover:opacity-80 active:scale-95" : "";

  if (graduated) {
    return (
      <button
        type="button"
        disabled={!editMode}
        onClick={onClick}
        className={`${base} bg-sky-100 text-sky-700 border border-sky-200 ${interactable}`}
        title="Graduated"
      >
        <AcademicCapIcon className="w-3.5 h-3.5 shrink-0" />
        GRAD
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={!editMode}
      onClick={onClick}
      className={`${base} bg-stone-100 text-stone-400 border border-stone-200 ${interactable}`}
      title="Not yet graduated"
    >
      <AcademicCapIcon className="w-3.5 h-3.5 shrink-0" />
      GRAD
    </button>
  );
}

// ─── Quick-record modal ───────────────────────────────────────────────────────

interface RecordTarget {
  camperId: number;
  camperName: string;
  ac: AchievementCredit;
  existing: RosterCompletion | null;
}

function RecordModal({
  target,
  onClose,
  onSaved,
}: {
  target: RecordTarget;
  onClose: () => void;
  onSaved: (camperId: number, acId: number, completion: RosterCompletion) => void;
}) {
  const [status, setStatus] = useState<CompletionStatus>(target.existing?.status ?? "completed");
  const [completedDate, setCompletedDate] = useState(target.existing?.completedDate ?? "");
  const [notes, setNotes] = useState(target.existing?.notes ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await completionsApi.create({
        camperId: target.camperId,
        achievementCreditId: target.ac.id,
        status,
        completedDate: status === "completed" ? completedDate || undefined : undefined,
        notes: notes || undefined,
      });
      onSaved(target.camperId, target.ac.id, {
        id: result.id,
        status: result.status as CompletionStatus,
        completedDate: result.completedDate ?? null,
        notes: result.notes ?? null,
      });
      toast.success(`${target.ac.code} recorded for ${target.camperName}`);
      onClose();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${target.ac.code} — ${target.camperName}`}
    >
      <div className="space-y-4">
        <p className="text-sm text-stone-500">{target.ac.name}</p>

        <Select
          label="Status"
          options={[
            { value: "completed", label: "Completed" },
            { value: "pending", label: "Pending" },
            { value: "excused", label: "Excused" },
          ]}
          value={status}
          onChange={(e) => setStatus(e.target.value as CompletionStatus)}
        />

        {status === "completed" && (
          <Input
            label="Date Completed"
            type="date"
            value={completedDate}
            onChange={(e) => setCompletedDate(e.target.value)}
          />
        )}

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">
            Notes (optional)
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes…"
            className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
          />
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Graduation record modal ──────────────────────────────────────────────────

interface GraduationTarget {
  camperId: number;
  camperName: string;
  graduationAcId: number;
  existingDate: string | null;
  existingCompletionId: number | null;
}

function GraduationModal({
  target,
  onClose,
  onSaved,
}: {
  target: GraduationTarget;
  onClose: () => void;
  onSaved: (camperId: number, graduated: boolean, graduationDate: string | null, completionId: number | null) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [graduationDate, setGraduationDate] = useState(target.existingDate ?? today);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await completionsApi.create({
        camperId: target.camperId,
        achievementCreditId: target.graduationAcId,
        status: "completed",
        completedDate: graduationDate || undefined,
        notes: notes || undefined,
      });
      onSaved(target.camperId, true, result.completedDate ?? null, result.id);
      toast.success(`Graduation recorded for ${target.camperName}`);
      onClose();
    } catch {
      toast.error("Failed to save graduation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Graduate — ${target.camperName}`}
    >
      <div className="space-y-4">
        <p className="text-sm text-stone-500">Record graduation from this activity.</p>

        <Input
          label="Graduation Date"
          type="date"
          value={graduationDate}
          onChange={(e) => setGraduationDate(e.target.value)}
        />

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">
            Notes (optional)
          </label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes…"
            className="block w-full rounded-lg border border-stone-300 text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-transparent"
          />
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={handleSave}>Record Graduation</Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ActivityProgress() {
  const { id } = useParams<{ id: string }>();
  const [year, setYear] = useState(currentYear);
  const [roster, setRoster] = useState<ActivityRoster | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [recordTarget, setRecordTarget] = useState<RecordTarget | null>(null);
  const [graduationTarget, setGraduationTarget] = useState<GraduationTarget | null>(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    activitiesApi
      .getRoster(Number(id), year)
      .then(setRoster)
      .catch(() => setRoster(null))
      .finally(() => setLoading(false));
  }, [id, year]);

  useEffect(() => { load(); }, [load]);

  // Patch a single completion in local state without refetching
  const patchCompletion = (camperId: number, acId: number, completion: RosterCompletion) => {
    setRoster((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        divisions: prev.divisions.map((div) => ({
          ...div,
          cabins: div.cabins.map((cabin) => ({
            ...cabin,
            campers: cabin.campers.map((c) =>
              c.id === camperId
                ? { ...c, completions: { ...c.completions, [acId]: completion } }
                : c
            ),
          })),
        })),
      };
    });
  };

  // Patch graduation status in local state
  const patchGraduation = (camperId: number, graduated: boolean, graduationDate: string | null, completionId: number | null) => {
    setRoster((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        divisions: prev.divisions.map((div) => ({
          ...div,
          cabins: div.cabins.map((cabin) => ({
            ...cabin,
            campers: cabin.campers.map((c) =>
              c.id === camperId
                ? { ...c, graduated, graduationDate, graduationCompletionId: completionId }
                : c
            ),
          })),
        })),
      };
    });
  };

  const openRecord = (camper: RosterCamper, ac: AchievementCredit) => {
    setRecordTarget({
      camperId: camper.id,
      camperName: `${camper.firstName} ${camper.lastName}`,
      ac,
      existing: camper.completions[ac.id] ?? null,
    });
  };

  const openGraduation = (camper: RosterCamper) => {
    const graduationAcId = roster?.activity.graduationAcId;
    if (!graduationAcId) return;
    setGraduationTarget({
      camperId: camper.id,
      camperName: `${camper.firstName} ${camper.lastName}`,
      graduationAcId,
      existingDate: camper.graduationDate,
      existingCompletionId: camper.graduationCompletionId,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!roster) {
    return (
      <div className="p-8 text-center text-stone-500">
        Activity not found.{" "}
        <Link to="/activities" className="text-forest-600 underline">Back to activities</Link>
      </div>
    );
  }

  const acs = sortAcs(roster.activity.achievementCredits);
  const hasGraduation = !!roster.activity.graduationAcId;
  const totalCampers = roster.divisions.reduce(
    (sum, div) => sum + div.cabins.reduce((s, c) => s + c.campers.length, 0),
    0
  );

  return (
    <div>
      <PageHeader
        title={roster.activity.name}
        subtitle="AC Progress by Cabin"
        actions={
          <Button
            variant={editMode ? "primary" : "outline"}
            icon={editMode ? <EyeIcon className="w-4 h-4" /> : <PencilSquareIcon className="w-4 h-4" />}
            onClick={() => setEditMode((e) => !e)}
          >
            {editMode ? "View Mode" : "Edit Mode"}
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Nav + controls */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link
            to={`/activities/${id}`}
            className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Back to Activity
          </Link>

          <div className="flex items-center gap-3">
            <span className="text-sm text-stone-500">{totalCampers} camper{totalCampers !== 1 ? "s" : ""}</span>
            <div className="w-32">
              <Select
                options={YEAR_OPTIONS}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-stone-500">
          <span className="font-medium">Legend:</span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-forest-100 text-forest-700 border border-forest-200">
            <CheckCircleSolid className="w-3.5 h-3.5" /> Completed
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
            <ClockIcon className="w-3.5 h-3.5" /> Pending
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-stone-100 text-stone-400 border border-stone-200 line-through">
            <XCircleIcon className="w-3.5 h-3.5" /> Excused
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-stone-100 text-stone-500 border border-stone-200">
            <MinusCircleIcon className="w-3.5 h-3.5" /> Not started
          </span>
          {hasGraduation && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
              <AcademicCapIcon className="w-3.5 h-3.5" /> Graduated
            </span>
          )}
          {editMode && <span className="text-forest-600 font-medium">Click any badge to record</span>}
        </div>

        {/* AC header row */}
        {(acs.length > 0 || hasGraduation) && (
          <div className="hidden lg:flex items-center gap-2 pl-[220px]">
            {acs.map((ac) => (
              <div key={ac.id} className="w-16 text-center text-xs font-medium text-stone-500 truncate" title={ac.name}>
                {ac.displayNumber}
              </div>
            ))}
            {hasGraduation && (
              <div className="w-16 text-center text-xs font-semibold text-sky-600 truncate" title="Graduation">
                GRAD
              </div>
            )}
          </div>
        )}

        {roster.divisions.length === 0 && (
          <div className="text-center py-12 text-stone-400 text-sm">
            No campers enrolled for {year}. Enrollments can be added from the admin panel.
          </div>
        )}

        {/* Divisions */}
        {roster.divisions.map((division) => (
          <div key={division.id ?? "unassigned"} className="space-y-4">
            {/* Division heading */}
            <h2 className="text-sm font-semibold text-stone-400 uppercase tracking-wider">
              {normalizeDivisionName(division.name)}
            </h2>

            {division.cabins.filter((c) => isCamperCabin({ name: c.name })).map((cabin) => (
              <div key={cabin.id ?? "unassigned"} className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                {/* Cabin header */}
                <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                  <span className="text-sm font-semibold text-stone-700">{formatCabinNumber(cabin.name)}</span>
                  <span className="text-xs text-stone-400">{cabin.campers.length} camper{cabin.campers.length !== 1 ? "s" : ""}</span>
                </div>

                {/* Campers */}
                <div className="divide-y divide-stone-100">
                  {cabin.campers.map((camper) => {
                    const completedCount = Object.values(camper.completions).filter(
                      (c) => c?.status === "completed"
                    ).length;

                    return (
                      <div key={camper.id} className="flex items-center gap-3 px-4 py-3">
                        {/* Name + progress fraction */}
                        <div className="w-48 shrink-0 flex items-center justify-between">
                          <span className="text-sm font-medium text-stone-800 truncate">
                            {camper.lastName}, {camper.firstName}
                          </span>
                          <span className="text-xs tabular-nums text-stone-400 ml-2 shrink-0">
                            {completedCount}/{acs.length}
                          </span>
                        </div>

                        {/* AC badges */}
                        <div className="flex flex-wrap gap-1.5">
                          {acs.map((ac) => (
                            <AcBadge
                              key={ac.id}
                              ac={ac}
                              completion={camper.completions[ac.id] ?? null}
                              editMode={editMode}
                              onClick={editMode ? () => openRecord(camper, ac) : undefined}
                            />
                          ))}
                          {/* Graduation badge — shown after standard ACs */}
                          {hasGraduation && (
                            <GraduationBadge
                              graduated={camper.graduated}
                              editMode={editMode}
                              onClick={editMode ? () => openGraduation(camper) : undefined}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Quick-record modal */}
      {recordTarget && (
        <RecordModal
          target={recordTarget}
          onClose={() => setRecordTarget(null)}
          onSaved={(camperId, acId, completion) => {
            patchCompletion(camperId, acId, completion);
            setRecordTarget(null);
          }}
        />
      )}

      {/* Graduation modal */}
      {graduationTarget && (
        <GraduationModal
          target={graduationTarget}
          onClose={() => setGraduationTarget(null)}
          onSaved={(camperId, graduated, graduationDate, completionId) => {
            patchGraduation(camperId, graduated, graduationDate, completionId);
            setGraduationTarget(null);
          }}
        />
      )}
    </div>
  );
}
